/**
 * @file: getFacadeItemResult.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import {
  ImageAnnotationMetadata,
  Tag,
} from '@euler/app/flows/media/annotation/functions/AnnotationMetadata';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { InspectionItemOptionInfo, InspectionSiteInfo } from '@euler/model';
import { AbnormalLevel, SeverityLevel } from '@euler/model/enum';
import {
  getTargetFacadeSiteOptionInfo,
  isRecordOnlySite,
} from './isRecordOnlySite';

export type FacadeItemResult = {
  abnormalLevel: AbnormalLevel;
  severityLevel: SeverityLevel;
  option: InspectionItemOptionInfo | undefined;
};

/**
 * The inspection result of a facade check item, derived from the results its
 * photos are tagged with: the most defective one wins.
 *
 * A photo carrying no tagged result — annotation turned off, tag deleted, or
 * a site with a single abnormal option — counts as the site's default
 * abnormal result, which is what the item took before per-photo results
 * were selectable.
 */
export const getFacadeItemResult = (
  site: InspectionSiteInfo,
  medias: { annotationMetadata?: ImageAnnotationMetadata }[],
): FacadeItemResult => {
  const defaultResult = getTargetFacadeSiteOptionInfo(site);

  if (isRecordOnlySite(site)) return defaultResult;

  if (!medias.length) {
    return {
      abnormalLevel: AbnormalLevel.Fine,
      severityLevel: SeverityLevel.None,
      option: site.checkItems[0]?.options.find(
        x => x.abnormalLevel === AbnormalLevel.Fine,
      ),
    };
  }

  let result: FacadeItemResult | undefined = undefined;

  for (const media of medias) {
    const mediaResult = getMediaResult(site, media) ?? defaultResult;
    if (!result || isMoreDefective(mediaResult, result)) {
      result = mediaResult;
    }
  }

  return result ?? defaultResult;
};

/** the most defective result the tags of a single photo are tagged with. */
const getMediaResult = (
  site: InspectionSiteInfo,
  media: { annotationMetadata?: ImageAnnotationMetadata },
): FacadeItemResult | undefined => {
  const tags = (media.annotationMetadata?.objects?.filter(
    x => x.type === 'tag',
  ) ?? []) as Tag[];

  let result: FacadeItemResult | undefined = undefined;

  for (const tag of tags) {
    if (!tag.option) continue;
    // the inventory stays authoritative for the label / maintenance advice
    // carried over to the item; a result no longer offered by the site is
    // ignored so that the site's default result applies instead.
    const option = site.checkItems[0]?.options.find(
      x => x.id === tag.option!.id,
    );
    if (!option) continue;
    const tagResult: FacadeItemResult = {
      abnormalLevel: option.abnormalLevel,
      severityLevel: option.severityLevel,
      option,
    };
    if (!result || isMoreDefective(tagResult, result)) {
      result = tagResult;
    }
  }

  return result;
};

const isMoreDefective = (x: FacadeItemResult, y: FacadeItemResult) => {
  const [levelX, levelY] = [
    getDefectiveLevel(x.abnormalLevel, x.severityLevel),
    getDefectiveLevel(y.abnormalLevel, y.severityLevel),
  ];
  if (levelX !== levelY) return levelX > levelY;
  return x.severityLevel > y.severityLevel;
};
